<template>
  <v-dialog v-model="showDialog" :max-width="800" :persistent="loading">
    <v-card class="pa-3">
      <div class="text-right d-flex text-white bg-black pa-2">
        <div class="text-left pa-0">Actualizar pagaré</div>
        <v-icon
          class="ml-auto"
          size="20"
          color="#fff"
          :disabled="loading"
          @click="showDialog = false"
          >mdi-close-circle</v-icon
        >
      </div>
      <v-form ref="customerRequestForm" v-model="form.valid" lazy-validation>
        <v-text-field
          v-model="form.customerId"
          label="ID del cliente"
          :disabled="true"
        />
        <v-text-field
          ref="promissory_note_amount"
          v-model="form.promissoryNoteAmount"
          type="number"
          prefix="$"
          filled
          label="Monto de pagaré*"
          persistent-hint
          step="100"
          :rules="promissoryNoteAmountRules()"
          :disabled="loading"
          :hint="`Monto de pagaré: ${formatNumber(
            form.promissoryNoteAmount ?? 0,
            '$',
            0
          )}`"
        />
        <local-date-picker
          v-model="form.promissoryNoteDate"
          label="fecha de firma de pagaré"
          label-class="d-flex flex-column text-muted font-weight-bold"
          :rules="promissoryNoteDateRules()"
          required
        />
        <span> Nombre del aval*: </span>
        <v-text-field
          ref="bank_guarantee_name"
          :value="form.bankGuaranteeName"
          label="Nombre completo"
          :rules="bankGuaranteeNameRules()"
          :disabled="loading"
          counter="255"
          maxlength="255"
          @input="(v) => (form.bankGuaranteeName = v ? v.toUpperCase() : '')"
        />
        <file-input
          ref="doc_promissory_note"
          v-model="form.promissoryNote"
          label="Pagaré (dejar vacío para conservar el actual)"
          accept="application/pdf"
          :max-size-mb="5"
        />
        <div class="mt-3 d-flex justify-center">
          <v-btn :loading="loading" color="warning" @click="update"
            >Actualizar</v-btn
          >
          <v-btn :disabled="loading" @click="showDialog = false">Cerrar</v-btn>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>
<script>
  import moment from 'moment';
  import numbersFormats from '@/mixins/numbersFormats';
  import LocalDatePicker from './LocalDatePicker.vue';
  import FileInput from '@/components/buttons/FileInput.vue';
  import DateFormats from '@/core/constants/DateFormats';
  import { updatePromissoryNote } from '@/repositories/PromissoryNotes';

  export default {
    name: 'UpdatePromissoryNoteForm',
    components: {
      LocalDatePicker,
      FileInput,
    },
    mixins: [numbersFormats],
    props: {
      userId: {
        type: Number,
        required: true,
      },
      documentId: {
        type: Number,
        default: null,
      },
      promissoryNoteUrl: {
        type: String,
        default: '',
      },
      promissoryNoteAmount: {
        type: Number,
        default: 0,
      },
      promissoryNoteDate: {
        type: String,
        default: '',
      },
      bankGuaranteeName: {
        type: String,
        default: '',
      },
      customerId: {
        //sapId
        type: Number,
        default: null,
      },
      value: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        form: {
          id: null,
          promissoryNote: null,
          promissoryNoteAmount: 0,
          promissoryNoteDate: '',
          bankGuaranteeName: '',
          customerId: null,
          userId: null,
          valid: false,
        },
        loading: false,
      };
    },
    computed: {
      showDialog: {
        set(value) {
          this.$emit('input', value);
        },
        get() {
          return this.value;
        },
      },
    },
    watch: {
      value(isOpen) {
        if (isOpen) {
          this.setForm();
        }
      },
    },
    methods: {
      promissoryNoteAmountRules() {
        return [
          (v) => (v !== '' && v != null) || 'El monto es requerido',
          (v) => Number.isFinite(Number(v)) || 'El monto debe ser un número',
          (v) => Number(v) > 0 || 'El monto debe ser mayor a 0',
        ];
      },
      promissoryNoteDateRules() {
        return [
          (v) => !!v || 'La fecha de firma de pagaré es requerida',
          (v) => {
            if (!v) return true;
            const [y, m, d] = v.split('-').map(Number);
            const selectedDate = new Date(y, m - 1, d);
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            return (
              selectedDate <= today ||
              'La fecha de firma de pagaré no puede ser futura'
            );
          },
        ];
      },
      bankGuaranteeNameRules() {
        return [
          (v) => !!v || 'Este campo es requerido',
          (v) =>
            !v ||
            v.length <= 255 ||
            'El nombre de la garantía bancaria no puede exceder 255 caracteres',
          (v) =>
            !v ||
            !/[^a-zA-Z0-9\s]/.test(v) ||
            'El nombre de la garantía bancaria solo puede contener letras, números y espacios',
        ];
      },
      async update() {
        if (!this.validateForm()) return;
        this.loading = true;
        try {
          await updatePromissoryNote(this.form.id, this.form);
          this.$toast.success('El pagaré se ha actualizado correctamente');
          this.showDialog = false;
          this.$emit('updated');
        } catch (error) {
          this.$toast.warning(
            'Ocurrió un error al enviar la solicitud. Por favor intente más tarde'
          );
        }
        this.loading = false;
      },
      validateForm() {
        const isValid = this.$refs.customerRequestForm.validate();
        if (!this.form.promissoryNoteDate) {
          this.$toast.warning('La fecha de firma de pagaré es requerida');
          return false;
        }
        if (!this.form.userId) {
          this.$toast.warning(
            'No se puede actualizar el pagaré sin un usuario válido'
          );
          return false;
        }
        return isValid;
      },
      normalizeDate(date) {
        if (!date) return '';
        const parsed = moment(date, DateFormats.ISO_DATE, true);
        return parsed.isValid() ? parsed.format(DateFormats.ISO_DATE) : '';
      },
      setForm() {
        this.form = {
          id: this.documentId,
          promissoryNote: null,
          promissoryNoteAmount: this.promissoryNoteAmount ?? 0,
          promissoryNoteDate: this.normalizeDate(this.promissoryNoteDate),
          bankGuaranteeName: this.bankGuaranteeName ?? 'SIN AVAL',
          customerId: this.customerId,
          userId: this.userId,
          valid: false,
        };
        this.$refs.customerRequestForm?.resetValidation();
      },
    },
  };
</script>
